import React, { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { langFormatter } from '@/utils/languageFormat';
import { ErrorModal, Button, Input } from '@/ui/index';
import generateInitialToken from '@/utils/generateInitialToken';
import {
  actions as cabinetActions,
  selectors as cabinetSelectors,
  hooks as cabinetHooks,
} from '@/domains/cabinet';

import styles from './PopUpLogin.module.scss';

export const PopUpLogin = () => {
  const { t, lang } = useTranslation();
  const { isLogged, selectedTariff } = useSelector(
    cabinetSelectors.selectCabinet,
  );
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();
  const formattedLang = langFormatter({ lang });
  const [subscribeTrigger] = cabinetHooks.useSubscriberCheck();
  const [otpTrigger] = cabinetHooks.useOtpRequest();
  const cleanedPhoneNumber = (value) => {
    return value.replace(/[+-]/g, '');
  };
  const eventName =
    selectedTariff?.event_type?.charAt(0).toUpperCase() +
    selectedTariff?.event_type?.slice(1);
  const FormSchema = Yup.object().shape({
    phone: Yup.string()
      .matches(/^\+994-\d{2}-\d{3}-\d{2}-\d{2}$/, t('common:Required'))
      .required(t('common:Required')),
  });
  const formik = useFormik({
    initialValues: {
      phone: '',
    },
    validationSchema: FormSchema,
    isInitialValid: false,
    onSubmit: async (values) => {
      const data = {
        phone: values?.phone,
      };

      dispatch(cabinetActions.setPhoneNumber(cleanedPhoneNumber(data.phone)));
      try {
        const initialToken = generateInitialToken(
          cleanedPhoneNumber(data.phone),
          uuidv4(),
        );

        dispatch(cabinetActions.setInitialToken(initialToken));
        sessionStorage.setItem('initialToken', initialToken);

        await subscribeTrigger({
          msisdn: cleanedPhoneNumber(data.phone),
          lang: formattedLang,
          accessToken: isLogged && sessionStorage.getItem('accessToken'),
          oneTime: true,
        }).unwrap();
        otpStepHandler(cleanedPhoneNumber(data.phone));
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: `${eventName}_number_added`,
        });
      } catch (error) {
        setShowError(true);
        setErrorMessage(error.message);
      } finally {
        setTimeout(formik.resetForm, 300);
      }
    },
  });

  const submitHandler = (e) => {
    e.preventDefault();
    formik.handleSubmit();
  };
  const otpStepHandler = async (number) => {
    try {
      const res = await otpTrigger({
        phoneNumber: number,
        lang: formattedLang,
      }).unwrap();
      dispatch(cabinetActions.setUuId(res.uuid));
      dispatch(cabinetActions.setPopUpStep(2));
    } catch (error) {
      setShowError(true);
      setErrorMessage(error?.message);
    }
  };

  return (
    <div
      className={styles.newLogin}
      itemScope
      itemType='https://schema.org/CheckInAction'
    >
      <p>{t('cabinet:common.EnterNumber')}</p>
      <form className={styles.form} onSubmit={(e) => submitHandler(e)}>
        <div className={styles.loginForm}>
          <Input
            className={styles.input}
            onBlur={formik.handleBlur('phone')}
            error={formik.touched.phone && formik.errors.phone}
            value={formik.values.phone}
            type='tel'
            onChange={(value) => {
              const formattedPhone = formatPhoneNumber(value);
              formik.setFieldValue('phone', formattedPhone);
            }}
            label={t('common:Phone')}
            placeholder={'+994-XX-XXX-XX-XX'}
            mask={'+\\9\\9\\4-99-999-99-99'}
            data-testid='tariff-purchase-input'
          />
          <div className={styles.buttonWrapper}>
            {isLogged && (
              <Button
                variant='transparent'
                type='button'
                onClick={() => {
                  dispatch(cabinetActions.setPopUpStep(0));
                }}
                className={styles.buttonGoBack}
              >
                {t('common:Back')}
              </Button>
            )}

            <Button
              data-testid='tariff-purchase-input'
              className={`${isLogged && styles.buttonContinue}`}
            >
              {t('common:Continue')}
            </Button>
          </div>
        </div>
      </form>

      <ErrorModal
        active={showError}
        icon='/assets/error.png'
        setActive={() => setShowError(false)}
      >
        {errorMessage}
      </ErrorModal>
      {!isLogged && (
        <span className={styles.belongsText}>
          {t('cabinet:common.AlreadyHave')}{' '}
          <span
            onClick={() => {
              dispatch(cabinetActions.setTariffModal(false));
              window?.location?.assign('/login');
            }}
            className={styles.belongsText__inside}
          >
            {t('cabinet:common.Login')}
          </span>
        </span>
      )}
    </div>
  );
};
